import { Navigate, Route, Routes } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider } from './hooks/useAuth';
import { LocaleProvider, useLocale } from './hooks/useT';
import { BodyRouteClass } from './components/BodyRouteClass';
import { ScrollToTop } from './components/ScrollToTop';
import { SiteHeader } from './components/SiteHeader';
import { SiteFooter } from './components/SiteFooter';
import { LandingPage } from './pages/LandingPage';
import { PlayPage } from './pages/PlayPage';
import { PremiumPage } from './pages/PremiumPage';
import { TopDezoomPage } from './pages/TopDezoomPage';
import { CreditsPage } from './pages/CreditsPage';
import { ResultPage } from './pages/ResultPage';
import { LeaderboardPage } from './pages/LeaderboardPage';
import { LoginPage } from './pages/LoginPage';
import { AuthCallbackPage, AuthVerifyPage } from './pages/AuthPages';
import { CGUPage } from './pages/legal/CGUPage';
import { CGVPage } from './pages/legal/CGVPage';
import { MentionsLegalesPage } from './pages/legal/MentionsLegalesPage';
import { ConfidentialitePage } from './pages/legal/ConfidentialitePage';
import { DiscoverPage } from './pages/DiscoverPage';
import { ProfilePage } from './pages/ProfilePage';
import { AdminPage } from './pages/AdminPage';
import { ArchivesPage } from './pages/ArchivesPage';
import { ArchivePlayPage } from './pages/ArchivePlayPage';
import { ArchiveLeaderboardPage } from './pages/ArchiveLeaderboardPage';
import { FriendsPage } from './pages/FriendsPage';
import { PwaInstallPrompt } from './components/PwaInstallPrompt';
import { RouteSeo, SeoNoscriptFallback } from './components/PageMeta';
import { PrerenderSignal } from './components/PrerenderSignal';
import { HowToPlayPage } from './pages/HowToPlayPage';
import { ContactPage } from './pages/ContactPage';

/** Remount UI when locale changes so all `t()` calls pick up the new language. */
function LocalizedShell({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  return <div key={locale} className="localized-shell">{children}</div>;
}

export function App() {
  return (
    <LocaleProvider>
      <AuthProvider>
        <LocalizedShell>
      <RouteSeo />
      <SeoNoscriptFallback />
      <PrerenderSignal />
      <BodyRouteClass />
      <ScrollToTop />
      <SiteHeader />
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/decouvrir" element={<DiscoverPage />} />
        <Route path="/comment-jouer" element={<HowToPlayPage />} />
        <Route path="/play" element={<PlayPage />} />
        <Route path="/premium" element={<PremiumPage />} />
        <Route path="/top" element={<TopDezoomPage />} />
        <Route path="/gallery" element={<Navigate to="/top" replace />} />
        <Route path="/practice" element={<Navigate to="/play" replace />} />
        <Route path="/arena" element={<Navigate to="/play" replace />} />
        <Route path="/friends" element={<FriendsPage />} />
        <Route path="/leagues/*" element={<Navigate to="/play" replace />} />
        <Route path="/credits" element={<CreditsPage />} />
        <Route path="/result" element={<ResultPage />} />
        <Route path="/leaderboard/amis" element={<LeaderboardPage />} />
        <Route path="/leaderboard/amis/:period" element={<LeaderboardPage />} />
        <Route path="/leaderboard" element={<LeaderboardPage />} />
        <Route path="/leaderboard/:period" element={<LeaderboardPage />} />
        <Route path="/archives" element={<ArchivesPage />} />
        <Route path="/archives/:puzzleNumber/play" element={<ArchivePlayPage />} />
        <Route path="/archives/:puzzleNumber/leaderboard/amis" element={<ArchiveLeaderboardPage />} />
        <Route path="/archives/:puzzleNumber/leaderboard" element={<ArchiveLeaderboardPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/verify" element={<AuthVerifyPage />} />
        <Route path="/auth/callback" element={<AuthCallbackPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/cgu" element={<CGUPage />} />
        <Route path="/cgv" element={<CGVPage />} />
        <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
        <Route path="/confidentialite" element={<ConfidentialitePage />} />
      </Routes>
      <SiteFooter />
      <PwaInstallPrompt />
        </LocalizedShell>
      </AuthProvider>
    </LocaleProvider>
  );
}
